import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { WhatsappLogo, X } from "@phosphor-icons/react";

const WA_MSG = encodeURIComponent(
    "Hi Wibpro Academy 👋 — I'd like to know more about:"
);
const WA_LINK = `https://wa.me/919100560075?text=${WA_MSG}`;
const DISMISS_KEY = "wibpro_wa_dismissed_at";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h — re-show tomorrow

export default function WhatsAppBubble() {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const [dismissed, setDismissed] = useState(true);
    const [labelOpen, setLabelOpen] = useState(false);

    // Load dismissal state
    useEffect(() => {
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (raw && Date.now() - Number(raw) < DISMISS_TTL_MS) {
                setDismissed(true);
            } else {
                setDismissed(false);
            }
        } catch {
            setDismissed(false);
        }
    }, []);

    // Auto-open the "Chat with us" label 4s after first render (attention-grab)
    useEffect(() => {
        if (dismissed) return;
        const t = setTimeout(() => setLabelOpen(true), 4000);
        const t2 = setTimeout(() => setLabelOpen(false), 12000);
        return () => { clearTimeout(t); clearTimeout(t2); };
    }, [dismissed]);

    // Visibility rules
    const hideOnTestTaking = pathname.startsWith("/tests/");
    const hideForPremium = user?.is_premium;   // paid students don't need the pitch
    if (dismissed || hideOnTestTaking || hideForPremium) return null;

    const dismiss = (e) => {
        e.preventDefault();
        e.stopPropagation();
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            className="fixed bottom-6 right-6 z-50 flex items-center gap-3 print:hidden"
            data-testid="whatsapp-bubble"
            onMouseEnter={() => setLabelOpen(true)}
            onMouseLeave={() => setLabelOpen(false)}
        >
            {/* Speech-bubble label */}
            <div
                className={`hidden sm:block transition-all duration-300 origin-right ${
                    labelOpen ? "opacity-100 scale-100 translate-x-0" : "opacity-0 scale-95 translate-x-4 pointer-events-none"
                }`}
                data-testid="whatsapp-bubble-label"
            >
                <div className="relative bg-ink text-bone px-4 py-2.5 rounded-sm shadow-lg pr-8">
                    <div className="text-sm font-serif">Chat with Wibpro</div>
                    <div className="text-[10px] uppercase tracking-widest text-gold">Usually replies in an hour</div>
                    <button
                        onClick={dismiss}
                        aria-label="Dismiss"
                        data-testid="whatsapp-bubble-dismiss"
                        className="absolute top-1 right-1 w-5 h-5 grid place-items-center text-bone/60 hover:text-bone"
                    >
                        <X size={10} weight="bold"/>
                    </button>
                    {/* tail */}
                    <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 bg-ink rotate-45"></div>
                </div>
            </div>

            {/* Bubble */}
            <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat with Wibpro Academy on WhatsApp"
                data-testid="whatsapp-bubble-button"
                className="relative w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white shadow-xl shadow-emerald-900/25 grid place-items-center transition-colors duration-150"
            >
                <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-25"></span>
                <WhatsappLogo size={28} weight="fill" className="relative"/>
            </a>
        </div>
    );
}
